import React, { useState, useEffect } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, getDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import calendarApi from '../../services/calendarApi';

interface VietnameseDatePickerProps {
    isOpen: boolean;
    onClose: () => void;
    value: Date;
    onChange: (date: Date) => void;
    title?: string;
}

interface LunarDayData {
    day: number;
    month: number;
}

interface LunarData {
    [date: string]: LunarDayData;
}

// Weekdays starting with Monday
const WEEKDAYS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

// Vietnamese solar holidays (month-day format)
const SOLAR_HOLIDAYS: Record<string, string> = {
    '01-01': 'Tết Dương lịch',
    '04-30': 'Ngày Giải phóng',
    '05-01': 'Ngày Quốc tế Lao động',
    '09-02': 'Quốc khánh',
};

// Check if date is a lunar holiday
const isLunarHoliday = (lunar: LunarDayData | undefined): 'tet' | 'holiday' | null => {
    if (!lunar) return null;

    // Tết Nguyên Đán (1-3 tháng 1 âm) - special: both solar+lunar red
    if (lunar.month === 1 && lunar.day >= 1 && lunar.day <= 3) return 'tet';

    // Tất Niên (30 tháng Chạp) - special: both dates red
    if (lunar.month === 12 && lunar.day === 30) return 'tet';

    // Mồng 1, 15 hàng tháng (ngày sóc, rằm) - only lunar date red
    if (lunar.day === 1 || lunar.day === 15) return 'holiday';

    // Tết Đoan Ngọ (5/5 âm)
    if (lunar.month === 5 && lunar.day === 5) return 'holiday';

    // Tết Trung Thu (15/8 âm)
    if (lunar.month === 8 && lunar.day === 15) return 'holiday';

    // Tết Hàn Thực (3/3 âm)
    if (lunar.month === 3 && lunar.day === 3) return 'holiday';

    // Vu Lan (15/7 âm)
    if (lunar.month === 7 && lunar.day === 15) return 'holiday';

    return null;
};

// Check if date is a solar holiday
const isSolarHoliday = (date: Date): boolean => {
    const monthDay = format(date, 'MM-dd');
    return SOLAR_HOLIDAYS[monthDay] !== undefined;
};

const VietnameseDatePicker: React.FC<VietnameseDatePickerProps> = ({
    isOpen,
    onClose,
    value,
    onChange,
    title = 'Chọn ngày'
}) => {
    const [viewDate, setViewDate] = useState(value);
    const [lunarData, setLunarData] = useState<LunarData>({});
    const [isLoading, setIsLoading] = useState(false);

    // Fetch lunar data for current month view
    useEffect(() => {
        const fetchLunarData = async () => {
            setIsLoading(true);
            try {
                const year = viewDate.getFullYear();
                const month = viewDate.getMonth() + 1;
                const response = await calendarApi.getMonth(year, month);

                const data: LunarData = {};
                if (response.days) {
                    response.days.forEach((day: { date: string; lunar: { day: number; month: number } }) => {
                        data[day.date] = {
                            day: day.lunar.day,
                            month: day.lunar.month
                        };
                    });
                }
                setLunarData(data);
            } catch (error) {
                console.error('Failed to fetch lunar data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        if (isOpen) {
            fetchLunarData();
        }
    }, [viewDate, isOpen]);

    // Reset view to selected date when opening
    useEffect(() => {
        if (isOpen) {
            setViewDate(value);
        }
    }, [isOpen, value]);

    const monthStart = startOfMonth(viewDate);
    const monthEnd = endOfMonth(viewDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

    // Get day of week for first day, adjusted for Monday start
    // getDay returns 0=Sunday, 1=Monday, etc.
    // We need: Monday=0, Tuesday=1, ... Sunday=6
    const startDayOfWeek = (getDay(monthStart) + 6) % 7;

    // Create padding for days before month starts
    const paddingDays = Array(startDayOfWeek).fill(null);

    const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
    const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));

    const handleSelectDate = (date: Date) => {
        onChange(date);
        onClose();
    };

    const handleToday = () => {
        const today = new Date();
        setViewDate(today);
        onChange(today);
        onClose();
    };

    const formatLunarDay = (date: Date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        const lunar = lunarData[dateStr];
        if (!lunar) return '';

        if (lunar.day === 1) return `1/${lunar.month}`;
        return String(lunar.day);
    };

    const getLunarData = (date: Date): LunarDayData | undefined => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return lunarData[dateStr];
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4">
                        <div className="flex items-center justify-between mb-2">
                            <h3 className="text-lg font-semibold">{title}</h3>
                            <button
                                onClick={onClose}
                                className="p-1 hover:bg-white/20 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="text-2xl font-bold">
                            {format(value, 'EEEE, dd/MM/yyyy', { locale: vi })}
                        </div>
                    </div>

                    {/* Month Navigation */}
                    <div className="flex items-center justify-between p-3 border-b bg-gray-50">
                        <button
                            onClick={handlePrevMonth}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5 text-gray-700" />
                        </button>
                        <span className="font-semibold text-gray-800">
                            Tháng {viewDate.getMonth() + 1}, {viewDate.getFullYear()}
                        </span>
                        <button
                            onClick={handleNextMonth}
                            className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                            <ChevronRight className="w-5 h-5 text-gray-700" />
                        </button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="p-3 bg-white">
                        {/* Weekday headers - Monday first */}
                        <div className="grid grid-cols-7 mb-2">
                            {WEEKDAYS.map((day, index) => (
                                <div
                                    key={day}
                                    className={`text-center text-xs font-semibold py-1 ${index === 6 ? 'text-red-500' : 'text-gray-600'
                                        }`}
                                >
                                    {day}
                                </div>
                            ))}
                        </div>

                        {/* Days grid */}
                        <div className="grid grid-cols-7 gap-1">
                            {/* Padding days */}
                            {paddingDays.map((_, index) => (
                                <div key={`pad-${index}`} className="aspect-square" />
                            ))}

                            {/* Actual days */}
                            {daysInMonth.map((date) => {
                                const isSelected = isSameDay(date, value);
                                const isToday = isSameDay(date, new Date());
                                const isSunday = getDay(date) === 0;
                                const lunar = getLunarData(date);
                                const lunarHoliday = isLunarHoliday(lunar);
                                const solarHoliday = isSolarHoliday(date);

                                // Determine if this is a "red date" (Tết or solar holiday for both, or lunar holiday for lunar only)
                                const isTet = lunarHoliday === 'tet';
                                const isHoliday = isTet || solarHoliday || lunarHoliday === 'holiday';
                                const lunarFirst = lunar?.day === 1;

                                return (
                                    <button
                                        key={date.toISOString()}
                                        onClick={() => handleSelectDate(date)}
                                        className={`
                                            aspect-square rounded-lg flex flex-col items-center justify-center
                                            text-sm transition-all relative
                                            ${isSelected
                                                ? 'bg-blue-500 text-white shadow-md'
                                                : isToday
                                                    ? 'bg-blue-100 text-blue-700 font-bold ring-2 ring-blue-300'
                                                    : 'hover:bg-gray-100 text-gray-800'
                                            }
                                            ${!isSelected && (isSunday || isTet || solarHoliday) ? 'text-red-500' : ''}
                                        `}
                                    >
                                        <span className="font-medium">{date.getDate()}</span>
                                        {!isLoading && (
                                            <span className={`text-[9px] leading-none ${isSelected
                                                    ? 'text-white/90'
                                                    : (isHoliday || lunarFirst)
                                                        ? 'text-red-500 font-medium'
                                                        : 'text-gray-500'
                                                }`}>
                                                {formatLunarDay(date)}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t p-3 flex justify-between bg-gray-50">
                        <button
                            onClick={handleToday}
                            className="px-4 py-2 text-blue-600 hover:bg-blue-100 rounded-lg font-medium transition-colors"
                        >
                            Hôm nay
                        </button>
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
                        >
                            Đóng
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default VietnameseDatePicker;
